import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, dashboardPathForRole } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAntiBot, HoneypotField, TurnstileWidget } from "../components/auth/AntiBotFields";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const antiBot = useAntiBot();

  React.useEffect(() => {
    if (user) navigate(dashboardPathForRole(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (antiBot.captchaConfig?.enabled && antiBot.captchaConfig?.required_login && !antiBot.captchaToken) {
      setError("Completa la verificación anti-bot.");
      return;
    }
    setSubmitting(true);
    const res = await login(email.trim(), password, {
      honeypot: antiBot.honeypot,
      form_loaded_at: antiBot.formLoadedAt,
      captcha_token: antiBot.captchaToken || undefined,
    });
    setSubmitting(false);
    if (!res.ok) {
      // Email no verificado: redirigir a pending con state.
      if (res.code === "EMAIL_NOT_VERIFIED") {
        toast.info("Debes confirmar tu email antes de ingresar.");
        navigate("/verify-email-pending", {
          state: { email: res.email, fromLogin: true, password },
          replace: true,
        });
        return;
      }
      setError(res.error);
      toast.error(res.error);
      antiBot.resetCaptcha();
      return;
    }
    toast.success("Sesión iniciada");
    const dest = location.state?.from || dashboardPathForRole(res.user.role);
    navigate(dest, { replace: true });
  };

  return (
    <div className="container-prose py-20">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="JustSapiens Procure — Inicio">
            <img
              src="/brand/logo-stacked-positive.png"
              alt="JustSapiens Procure"
              className="h-32 w-auto object-contain"
              loading="eager"
              decoding="async"
            />
          </Link>
        </div>
        <div className="border border-gray-200 bg-white p-8">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">Acceso</p>
            <h1 className="font-serif text-3xl text-gray-900 mb-2">Iniciar sesión</h1>
            <p className="text-sm text-gray-600">
              Entra a tu panel de JustSapiens Procure para gestionar tus casos.
            </p>
          </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="rounded-sm h-11"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="password">Contraseña</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-primary hover:underline"
                data-testid="login-forgot-password"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="rounded-sm h-11"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2"
            >
              {error}
            </div>
          )}

          <HoneypotField value={antiBot.honeypot} onChange={antiBot.setHoneypot} />
          <TurnstileWidget
            config={antiBot.captchaConfig}
            onToken={antiBot.setCaptchaToken}
            action="login"
          />

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-sm h-11"
            data-testid="login-submit-button"
          >
            {submitting ? "Entrando…" : "Entrar"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 mt-6 text-center">
          ¿No tienes cuenta?{" "}
          <Link to="/register" data-testid="login-go-register" className="text-primary hover:underline">
            Crear una
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
}
